/** @jsxImportSource @emotion/react */
import { ListFilterComponent } from "../../components/ListFilterComponent";
import { useGetProductCategories } from "../product-categories/service/product-categories-api";
import { useListProductTags } from "../product-tags/service/product-tags-api";
import { SpinnerBlock } from "../common/Spinner";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { useSearchParamsManipulation } from "../../hooks/use-searchparams-manipulation";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SerializedStyles } from "@emotion/react";
import { useGetKitchenCategories } from "../kitchen-categories/service/kitchen-categories-api";
import { useGetCountries } from "../../service/api";

type Props = {
  additionalStyling?: SerializedStyles;
  includeClassificationFilter?: boolean;
};

export const ProductsFilter = ({
  additionalStyling,
  includeClassificationFilter = false,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const {
    tags,
    setTags,
    removeTags,
    categories,
    setCategories,
    removeCategories,
    kitchenCategories,
    setKitchenCategories,
    removeKitchenCategories,
    currency,
    setCurrency,
    removeCurrency,
    classification,
    setClassification,
    removeClassification,
    country,
    setCountry,
    removeCountry,
  } = useSearchParamsManipulation();
  const {
    data: productTags,
    isLoading: isLoadingProductTags,
  } = useListProductTags(organizationKeyName);
  const {
    data: productCategories,
    isLoading: isLoadingProductCategories,
  } = useGetProductCategories(organizationKeyName);
  const {
    data: kitchenCategoriesData,
    isLoading: isLoadingKitchenCategories,
  } = useGetKitchenCategories(organizationKeyName);
  const { data: countries, isLoading: isLoadingCountries } = useGetCountries();

  const categoryFilterOptions = (productCategories || [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((category) => ({
      value: category.id,
      label: category.name,
    }));
  const tagFilterOptions = (productTags || []).map((tag) => ({
    value: tag.name,
    label: tag.name,
  }));
  const kitchenCategoryFilterOptions = (kitchenCategoriesData || []).map(
    (category) => ({
      value: category.id,
      label: category.name,
    })
  );
  const countryFilterOptions = (countries || [])
    .filter((country) => country.enabled)
    .map((country) => ({
      value: country.countryCode,
      label: country.englishName,
    }));

  const currencyFilterOptions = [
    {
      value: "EUR",
      label: "EUR",
    },
    {
      value: "DKK",
      label: "DKK",
    },
    {
      value: "NOK",
      label: "NOK",
    },
    {
      value: "ISK",
      label: "ISK",
    },
  ];

  const classificationFilterOptions = [
    {
      label: "Addition product",
      value: "production_addition",
    },
    {
      label: "Main product",
      value: "generic",
    },
  ];

  if (
    isLoadingProductCategories ||
    isLoadingProductTags ||
    isLoadingKitchenCategories ||
    isLoadingCountries
  ) {
    return <SpinnerBlock />;
  }

  return (
    <ListFilterComponent
      chosenFilters={[
        {
          label: "Product category",
          chosenValues: categories?.split(",") || [],
          onChangeValues: (values) => {
            if (values.length > 0) {
              setCategories(values);
            } else {
              removeCategories();
            }
          },
          removeFilter: () => {
            removeCategories();
          },
          options: categoryFilterOptions,
        },
        {
          label: "Kitchen category",
          chosenValues: kitchenCategories?.split(",") || [],
          onChangeValues: (values) => {
            if (values.length > 0) {
              setKitchenCategories(values);
            } else {
              removeKitchenCategories();
            }
          },
          removeFilter: () => {
            removeKitchenCategories();
          },
          options: kitchenCategoryFilterOptions,
        },
        {
          label: "Tag",
          chosenValues: tags?.split(",") || [],
          onChangeValues: (values) => {
            if (values.length > 0) {
              setTags(values);
            } else {
              removeTags();
            }
          },
          removeFilter: () => {
            removeTags();
          },
          options: tagFilterOptions,
        },
        {
          label: "Currencies",
          chosenValues: currency?.split(",") || [],
          onChangeValues: (values) => {
            if (values.length > 0) {
              setCurrency(values);
            } else {
              removeCurrency();
            }
          },
          removeFilter: () => {
            removeCurrency();
          },
          options: currencyFilterOptions,
        },
        {
          label: "Countries",
          chosenValues: country?.split(",") || [],
          onChangeValues: (values) => {
            if (values.length > 0) {
              setCountry(values);
            } else {
              removeCountry();
            }
          },
          removeFilter: () => {
            removeCountry();
          },
          options: countryFilterOptions,
        },
        ...(includeClassificationFilter
          ? [
              {
                label: "Product type",
                chosenValues: classification?.split(",") || [],
                onChangeValues: (values: string[]) => {
                  if (values.length > 0) {
                    setClassification(values);
                  } else {
                    removeClassification();
                  }
                },
                removeFilter: () => {
                  removeClassification();
                },
                options: classificationFilterOptions,
                singleSelect: true,
              },
            ]
          : []),
      ]}
      newFilterOptions={[
        ...(!categories
          ? [
              {
                name: "categories",
                label: "Product categories",
                icon: <FontAwesomeIcon icon={solid("layer-group")} />,
                options: categoryFilterOptions,
              },
            ]
          : []),
        ...(!tags
          ? [
              {
                name: "tags",
                label: "Tags",
                icon: <FontAwesomeIcon icon={solid("tag")} />,
                options: tagFilterOptions,
              },
            ]
          : []),
        ...(!kitchenCategories
          ? [
              {
                name: "kitchenCategories",
                label: "Kitchen categories",
                icon: <FontAwesomeIcon icon={solid("receipt")} />,
                options: kitchenCategoryFilterOptions,
              },
            ]
          : []),
        ...(!currency
          ? [
              {
                name: "currency",
                label: "Currencies",
                icon: <FontAwesomeIcon icon={solid("coins")} />,
                options: currencyFilterOptions,
              },
            ]
          : []),
        ...(!country
          ? [
              {
                name: "country",
                label: "Countries",
                icon: <FontAwesomeIcon icon={solid("globe")} />,
                options: countryFilterOptions,
              },
            ]
          : []),
        ...(includeClassificationFilter && !classification
          ? [
              {
                name: "classification",
                label: "Product type",
                icon: <FontAwesomeIcon icon={solid("tag")} />,
                options: classificationFilterOptions,
                singleSelect: true,
              },
            ]
          : []),
      ]}
      additionalStyling={additionalStyling}
      onCreateNewFilter={(filters) => {
        const categoriesFilter = filters.find((f) => f.name === "categories");
        const tagsFilter = filters.find((f) => f.name === "tags");
        const kitchenCategoriesFilter = filters.find(
          (f) => f.name === "kitchenCategories"
        );
        const currencyFilter = filters.find((f) => f.name === "currency");
        const classificationFilter = filters.find(
          (f) => f.name === "classification"
        );
        const countryFilter = filters.find((f) => f.name === "country");
        if (categoriesFilter?.choices && categoriesFilter.choices.length > 0) {
          setCategories(categoriesFilter?.choices || []);
        }
        if (tagsFilter?.choices && tagsFilter.choices.length > 0) {
          setTags(tagsFilter?.choices || []);
        }
        if (
          kitchenCategoriesFilter?.choices &&
          kitchenCategoriesFilter.choices.length > 0
        ) {
          setKitchenCategories(kitchenCategoriesFilter?.choices || []);
        }
        if (currencyFilter?.choices && currencyFilter.choices.length > 0) {
          setCurrency(currencyFilter?.choices || []);
        }
        if (
          classificationFilter?.choices &&
          classificationFilter.choices.length > 0
        ) {
          setClassification(classificationFilter?.choices || []);
        }
        if (countryFilter?.choices && countryFilter.choices.length > 0) {
          setCountry(countryFilter.choices || []);
        }
      }}
    />
  );
};
